// components/playground/Header/Header.jsx - FIXED FOR DROPDOWNS
import React, { useState, useEffect, useRef } from 'react';
import { Settings, ChevronDown } from 'lucide-react';
import { StandardHeader } from '../../common/StandardHeader';
import { PlaygroundSettingsModal } from '../sections/Default/PLSettingsModal';
import ScenarioDropdown from './ScenarioDropdown';
import ScenarioOverflowMenu from './ScenarioOverflowMenu';
import { useCacheRefresh } from '../../../hooks/useCacheRefresh';
import { usePagePreferences } from '../../../hooks/useUserPreference';

const header_name = "P&L Playground";

export const Header = ({ 
  loading, 
  lastUpdated,
  selectedYear,
  onYearChange,
  availableYears,
  currentYear,
  averagingMethod,
  onAveragingMethodChange,
  accountingMethod,
  onAccountingMethodChange,
  currentMonth,
  
  scenarios = [],
  currentScenarioId,
  currentScenarioName = 'Baseline',
  hasUnsavedScenarioChanges = false,
  
  onSelectScenario,
  onSaveScenario,
  onUpdateScenario,
  onSaveAsNew,
  onRenameScenario,
  onDeleteScenario,
  onDiscardChanges,
  onOpenScenarioManager,
  onRefreshScenarios,

  isLoadingScenario,
  isSavingScenario,
  isRefreshingScenarios,
}) => {
  const { refreshPlaygroundCache, isRefreshingPlayground } = useCacheRefresh();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);

  const [preferences, setPreference, setMultiplePreferences, prefsLoading, prefsError, prefsInitialized] = usePagePreferences(
    'pl_scenario_playground',
    {
      settings: {
        accounting_method: 'Accrual',
        projection_method: 'last12'
      }
    }
  );

  const initializedRef = useRef(false);
  const isSettingsLoadRef = useRef(false);

  useEffect(() => {
    if (prefsInitialized && !initializedRef.current) {
      isSettingsLoadRef.current = true;

      const savedAccountingMethod = preferences.settings?.accounting_method || 'Accrual';
      const savedProjectionMethod = preferences.settings?.projection_method || 'ytd';
      
      if (savedAccountingMethod !== accountingMethod) {
        onAccountingMethodChange({ target: { value: savedAccountingMethod } });
      }
      if (savedProjectionMethod !== averagingMethod) {
        onAveragingMethodChange({ target: { value: savedProjectionMethod } });
      }
      
      initializedRef.current = true;
      
      setTimeout(() => {
        isSettingsLoadRef.current = false;
      }, 200);
    }
  }, [prefsInitialized, preferences.settings, accountingMethod, averagingMethod, onAccountingMethodChange, onAveragingMethodChange]);

  const handleSaveSettings = async (newSettings) => {
    setIsSavingSettings(true);
    isSettingsLoadRef.current = true;
    
    try {
      onAccountingMethodChange({ target: { value: newSettings.accountingMethod } });
      onAveragingMethodChange({ target: { value: newSettings.averagingMethod } });
      
      if (prefsInitialized) {
        await setPreference('settings', {
          accounting_method: newSettings.accountingMethod,
          projection_method: newSettings.averagingMethod
        });
      }
      
      await refreshPlaygroundCache({ 
        year: selectedYear,
        accountingMethod: newSettings.accountingMethod 
      });
      
      setIsSettingsOpen(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setIsSavingSettings(false);
      setTimeout(() => {
        isSettingsLoadRef.current = false;
      }, 200);
    }
  };

  const handleRefresh = () => {
    refreshPlaygroundCache({ 
      year: selectedYear,
      accountingMethod 
    });
  };

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1] || '';
  };

  const getProjectionMethodLabel = () => {
    if (averagingMethod === 'last12') return 'Last 12 Months';
    if (averagingMethod === 'last3') return 'Last 3 Months';
    if (averagingMethod === 'last6') return 'Last 6 Months';
    return '';
  };

  // ✅ FIX: Calculate actual vs projected months using year-aware logic
  const getMonthCounts = () => {
    const today = new Date();
    const actualCurrentYear = today.getFullYear();
    const actualCurrentMonth = today.getMonth() + 1; // 1-12

    if (selectedYear < actualCurrentYear) {
      // Past year: all 12 months are actual
      return {
        actualMonths: 12,
        projectedMonths: 0,
        isHistorical: true
      };
    } else if (selectedYear > actualCurrentYear) {
      // Future year: all 12 months are projected
      return {
        actualMonths: 0,
        projectedMonths: 12,
        isFuture: true
      };
    } else {
      // Current year: months before current month are actual
      // currentMonth and later are projected
      const actual = actualCurrentMonth - 1; // Completed months
      const projected = 12 - actual;
      
      return {
        actualMonths: actual,
        projectedMonths: projected,
        isCurrent: true,
        lastActualMonth: actual > 0 ? getMonthName(actual) : null
      };
    }
  };

  const monthCounts = getMonthCounts();

  const isBaseline = !currentScenarioId || currentScenarioName === 'Baseline';

  const getSaveButtonText = () => {
    if (isBaseline) return 'Save as Scenario';
    return 'Update Scenario';
  };

  const additionalTimestamps = (
    <div className="text-sm text-blue-100 truncate">
      {monthCounts.isHistorical ? (
        <>{selectedYear} Hist • {accountingMethod}</>
      ) : monthCounts.isFuture ? (
        <>Proj • {accountingMethod}</>
      ) : monthCounts.actualMonths > 0 ? (
        <>{monthCounts.actualMonths}A+{monthCounts.projectedMonths}P • {accountingMethod}</>
      ) : (
        <>Proj • {accountingMethod}</>
      )}
      {' • '}
      <span className={!isBaseline ? 'text-yellow-200 font-semibold' : ''}>
        {currentScenarioName}
      </span>
      {hasUnsavedScenarioChanges && <span className="text-orange-300 font-bold"> *unsaved</span>}
    </div>
  );


  return (
    <>
      <StandardHeader
        title={header_name}
        subtitle={additionalTimestamps}
        loading={loading}
        lastUpdated={lastUpdated}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshingPlayground}
        customControls={
          /* ⬇️ CRITICAL: NO wrapper with overflow-hidden around customControls */
          <div className="w-full">
            {/* Desktop Controls */}
            <div className="hidden lg:flex items-center gap-2 flex-wrap min-w-0">
              
              {/* Year Selector */}
              <select
                value={selectedYear}
                onChange={(e) => onYearChange(parseInt(e.target.value))}
                className="flex-shrink-0 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                {availableYears.map(year => {
                  const currentYear = new Date().getFullYear();
                  let label;
                  
                  if (year < currentYear) {
                    label = `${year} (Historical)`; 
                  } else if (year === currentYear) {
                    label = `${year} (Current)`;
                  } else {
                    label = `${year} (Projected)`;
                  }
                  
                  return (
                    <option key={year} value={year}>
                      {label}
                    </option>
                  );
                })}
              </select>
              
              {/* Scenario Dropdown - NO wrapper */}
              <div className="flex-shrink-0 min-w-0">
                <ScenarioDropdown
                  currentScenarioName={currentScenarioName}
                  hasUnsavedChanges={hasUnsavedScenarioChanges}
                  scenarios={scenarios}
                  onSelectScenario={onSelectScenario}
                  onNewScenario={() => onSaveScenario('new')}
                />
              </div>

              {/* Overflow Menu - NO wrapper */}
              <div className="flex-shrink-0">
                <ScenarioOverflowMenu
                  isBaseline={isBaseline}
                  hasUnsavedChanges={hasUnsavedScenarioChanges}
                  onRename={onRenameScenario}
                  onSaveAsNew={onSaveAsNew}
                  onDiscardChanges={onDiscardChanges}
                  onDelete={onDeleteScenario}
                  onViewDetails={onOpenScenarioManager}
                  onUpdateScenario={onUpdateScenario}
                  onRefreshScenarios={onRefreshScenarios}
                  isRefreshing={isRefreshingScenarios}
                  isSaving={isSavingScenario}
                />
              </div>

              {/* Save Button */}
              {hasUnsavedScenarioChanges && (
                <button
                  onClick={onUpdateScenario}
                  disabled={isSavingScenario}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  title={getSaveButtonText()}
                >
                  <span className="text-base">💾</span>
                  <span className="whitespace-nowrap">{isSavingScenario ? 'Saving...' : getSaveButtonText()}</span>
                </button>
              )}
              
              {/* Settings Button */}
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="flex items-center justify-center px-4 py-2 bg-white text-blue-600 rounded-md hover:bg-blue-50 transition-colors font-medium text-sm border border-gray-300 flex-shrink-0"
                title="Configure scenario settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>

            {/* Mobile/Tablet Controls */}
            <div className="lg:hidden w-full">
              {/* Top Row */}
              <div className="flex items-center gap-2 w-full mb-2">
                <select
                  value={selectedYear}
                  onChange={(e) => onYearChange(parseInt(e.target.value))}
                  className="flex-1 min-w-0 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50"
                >
                  {availableYears.map(year => {
                    const currentYear = new Date().getFullYear();
                    let label;
                    
                    if (year < currentYear) {
                      label = `${year} (Hist.)`; 
                    } else if (year === currentYear) {
                      label = `${year}`;
                    } else {
                      label = `${year} (Proj.)`;
                    }
                    
                    return (
                      <option key={year} value={year}>
                        {label}
                      </option>
                    );
                  })}
                </select>

                <button
                  onClick={() => setIsSettingsOpen(true)}
                  className="flex items-center justify-center p-2 sm:px-3 sm:py-2 bg-white text-blue-600 rounded-md hover:bg-blue-50 transition-colors font-medium text-sm border border-gray-300 flex-shrink-0"
                  title="Settings"
                >
                  <Settings className="h-4 w-4" />
                </button>

                <button
                  onClick={() => setShowMobileControls(!showMobileControls)}
                  className="flex items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-xs sm:text-sm flex-shrink-0 min-w-[90px] sm:min-w-[110px]"
                >
                  <span className="truncate">Scenarios</span>
                  <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 transition-transform ${showMobileControls ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Expandable Scenario Controls */}
              {showMobileControls && (
                <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2 border border-gray-200 w-full">
                  <div className="w-full">
                    <ScenarioDropdown
                      currentScenarioName={currentScenarioName}
                      hasUnsavedChanges={hasUnsavedScenarioChanges}
                      scenarios={scenarios}
                      onSelectScenario={onSelectScenario}
                      onNewScenario={() => onSaveScenario('new')}
                      isMobile={true}
                    />
                  </div>

                  <div className="flex items-center gap-2 w-full">
                    {hasUnsavedScenarioChanges && (
                      <button
                        onClick={onUpdateScenario}
                        disabled={isSavingScenario}
                        className="flex-1 min-w-0 flex items-center justify-center gap-1.5 px-2 sm:px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all font-semibold text-xs sm:text-sm shadow-md disabled:opacity-50"
                      >
                        <span>💾</span>
                        <span className="truncate">{isSavingScenario ? 'Saving...' : 'Save'}</span>
                      </button>
                    )}

                    <div className="flex-shrink-0">
                      <ScenarioOverflowMenu
                        isBaseline={isBaseline}
                        hasUnsavedChanges={hasUnsavedScenarioChanges}
                        onRename={onRenameScenario}
                        onSaveAsNew={onSaveAsNew}
                        onDiscardChanges={onDiscardChanges}
                        onDelete={onDeleteScenario}
                        onViewDetails={onOpenScenarioManager}
                        onUpdateScenario={onUpdateScenario}
                        onRefreshScenarios={onRefreshScenarios}
                        isRefreshing={isRefreshingScenarios}
                        isSaving={isSavingScenario}
                        isMobile={true}
                      />
                    </div>
                  </div>

                  {hasUnsavedScenarioChanges && (
                    <div className="text-xs text-orange-600 font-medium text-center py-1.5 bg-orange-50 rounded">
                      ⚠️ You have unsaved changes
                    </div>
                  )}
                </div>
              )}

              {/* Quick Save Bar */}
              {!showMobileControls && hasUnsavedScenarioChanges && (
                <button
                  onClick={onUpdateScenario}
                  disabled={isSavingScenario}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all font-semibold text-sm shadow-md"
                >
                  <span>💾</span>
                  <span className="truncate">{isSavingScenario ? 'Saving...' : getSaveButtonText()}</span>
                </button>
              )}
            </div>
          </div>
        }
      />

      <PlaygroundSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        accountingMethod={accountingMethod}
        averagingMethod={averagingMethod}
        onSave={handleSaveSettings}
        currentMonth={currentMonth}
        isSaving={isSavingSettings}
      />
    </>
  );
};